import { useState, useCallback, useEffect, useRef } from "react";
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  type Node,
  type NodeTypes,
  type EdgeTypes,
  type ReactFlowInstance,
  type NodeMouseHandler,
} from "reactflow";
import "reactflow/dist/style.css";
import type { RoadmapArea, RoadmapNode } from "../../../data/ch_concepts";
import { AreaGroupNode } from "./AreaGroupNode";
import { TopicsGroupNode } from "./TopicsGroupNode";
import { AnimatedEdge } from "./AnimatedEdge";
import { useRoadmapLayout } from "./useRoadmapLayout";
import { useGraphRevealAnimation } from "./useGraphRevealAnimation";
// ─── CSS for animated dash ─────────────────────────────────────────────────────

const edgeDashStyle = `
@keyframes roadmapEdgeDash {
  to { stroke-dashoffset: -20; }
}
`;

// ─── Node/edge type registrations (stable refs) ───────────────────────────────

const nodeTypes: NodeTypes = {
  areaGroup: AreaGroupNode,
  topicsGroup: TopicsGroupNode,
};

const edgeTypes: EdgeTypes = {
  roadmapEdge: AnimatedEdge,
};

// ─── Props ─────────────────────────────────────────────────────────────────────

interface RoadmapGraphProps {
  areas: RoadmapArea[];
  isGenerating?: boolean;
  isMobile?: boolean;
  onTopicSelect: (node: RoadmapNode, areaLabel: string, areaDescription: string) => void;
}

export function RoadmapGraph({
  areas,
  isGenerating = false,
  isMobile = false,
  onTopicSelect,
}: RoadmapGraphProps) {
  const [expandedAreaIds, setExpandedAreaIds] = useState<Set<string>>(new Set());
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  const initialFitDone = useRef(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);

  // Auto-expand first non-known area on initial load
  useEffect(() => {
    if (areas.length > 0 && expandedAreaIds.size === 0) {
      const first = areas.find((a) => a.status !== "known");
      if (first) {
        setExpandedAreaIds(new Set([first.id]));
      }
    }
  }, [areas.length]);

  const onToggleArea = useCallback((areaId: string) => {
    setExpandedAreaIds((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  }, []);

  const onTopicClick = useCallback(
    (node: RoadmapNode, areaLabel: string, areaDescription: string) => {
      onTopicSelect(node, areaLabel, areaDescription);
    },
    [onTopicSelect],
  );

  // Handle all node clicks at the ReactFlow level
  const handleNodeClick: NodeMouseHandler = useCallback((event: React.MouseEvent, node: Node) => {
    if (node.type === "areaGroup") {
      const areaId = node.data?.area?.id;
      if (areaId) onToggleArea(areaId);
    } else if (node.type === "topicsGroup") {
      // Find which topic row was clicked via data-topic-index attribute
      const target = event.target as HTMLElement;
      const row = target.closest("[data-topic-index]") as HTMLElement | null;
      if (row) {
        const idx = parseInt(row.dataset.topicIndex ?? "", 10);
        const topics = node.data?.topics;
        if (topics && !isNaN(idx) && topics[idx]) {
          onTopicClick(topics[idx], node.data.areaLabel, node.data.areaDescription);
        }
      }
    }
  }, [onToggleArea, onTopicClick]);

  // Compute layout
  const layout = useRoadmapLayout({
    areas,
    expandedAreaIds,
    onToggleArea,
    onTopicClick,
  });

  // Apply reveal animation
  const { nodes: animatedNodes, edges: animatedEdges } = useGraphRevealAnimation(
    layout.nodes,
    layout.edges,
    shouldAnimate,
  );

  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState(animatedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(animatedEdges);

  // Sync layout changes into ReactFlow state
  useEffect(() => {
    setNodes(animatedNodes);
    setEdges(animatedEdges);
  }, [animatedNodes, animatedEdges, setNodes, setEdges]);

  // Fit view after layout changes
  useEffect(() => {
    if (!rfInstance || !nodes.length) return;
    const timer = setTimeout(() => {
      rfInstance.fitView({ padding: 0.15, duration: initialFitDone.current ? 400 : 0 });
      initialFitDone.current = true;
    }, initialFitDone.current ? 50 : 0);
    return () => clearTimeout(timer);
  }, [rfInstance, layout]);

  // Stop entrance animation after initial reveal
  useEffect(() => {
    if (!shouldAnimate) return;
    const maxDelay = Math.max(
      ...animatedNodes.map((n) => (n.data?.revealDelay ?? 0) * 1000),
      ...animatedEdges.map((e) => (e.data?.revealDelay ?? 0) * 1000),
    );
    const timer = setTimeout(() => setShouldAnimate(false), maxDelay + 800);
    return () => clearTimeout(timer);
  }, []);

  // Re-trigger animation when areas stream in during generation
  const prevAreaCount = useRef(areas.length);
  useEffect(() => {
    if (isGenerating && areas.length > prevAreaCount.current) {
      setShouldAnimate(true);
      const timer = setTimeout(() => setShouldAnimate(false), 1200);
      prevAreaCount.current = areas.length;
      return () => clearTimeout(timer);
    }
    prevAreaCount.current = areas.length;
    return undefined;
  }, [areas.length, isGenerating]);

  return (
    <div className="w-full h-full relative">
      <style>{edgeDashStyle}</style>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onInit={setRfInstance}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.3}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        panOnDrag
        panOnScroll
        zoomOnScroll={false}
        zoomOnPinch
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#e5e7eb" gap={20} size={1} />
      </ReactFlow>
    </div>
  );
}
